/**
 * Engagement heatmap: precinct × period grid with knock/conversation metrics.
 *
 * GET /narrative/engagement-heatmap?since_days=45&bucket=weekly&min_n=5
 *
 * Response includes suppressed cells (min_n filter applies server-side;
 * cells below the threshold are omitted from the cells array).
 */
import client from '@/api/client';

export interface HeatmapCell {
  precinct_num: string;
  period_start: string;
  knocks_attempted: number;
  conversations: number;
  positive_leaning: number;
  engagement_score: number;
}

export interface HeatmapResponse {
  since: string;
  until: string;
  bucket: 'week' | 'day';
  min_n: number;
  precincts: string[];
  periods: string[];
  cells: HeatmapCell[];
}

export interface FetchEngagementHeatmapOptions {
  since_days?: number;
  bucket?: 'weekly' | 'daily';
  min_n?: number;
}

/**
 * Fetch the engagement heatmap.
 *
 * Parameters:
 * - since_days: lookback window in days (default: 45)
 * - bucket: time bucket width — 'weekly' or 'daily' (default: 'weekly')
 * - min_n: minimum knock count for a cell to be included (K-anonymity;
 *   default: 5). Cells with knocks_attempted < min_n are suppressed.
 *
 * Returns a grid of precincts (rows) × periods (columns) with engagement metrics.
 */
export async function fetchEngagementHeatmap(
  params?: FetchEngagementHeatmapOptions
): Promise<HeatmapResponse> {
  const queryParams: Record<string, string | number> = {};
  if (params?.since_days !== undefined) queryParams.since_days = params.since_days;
  if (params?.bucket !== undefined) queryParams.bucket = params.bucket;
  if (params?.min_n !== undefined) queryParams.min_n = params.min_n;

  const response = await client.get<HeatmapResponse>(
    '/narrative/engagement-heatmap',
    { params: queryParams }
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Drill-down detail: GET /narrative/engagement-heatmap/detail
//   (Tier 2 enhancement, session 5.12 — 2026-04-16)
// ---------------------------------------------------------------------------

export interface HeatmapDetailRow {
  knockback_id: number;
  timestamp: string;
  outcome: string;
  canvasser_name: string | null;
  voter_hint: string;
  candidate_lean: string | null;
  vote_intent: string | null;
  top_issues: string[] | null;
  notes_snippet: string | null;
}

export interface HeatmapDetailResponse {
  precinct_num: string;
  period_start: string;
  period_end: string;
  bucket: 'daily' | 'weekly' | 'monthly';
  total_knocks: number;
  knocks: HeatmapDetailRow[];
  has_more: boolean;
  next_offset: number | null;
  k_anonymity_applied: boolean;
  canvasser_names_redacted: boolean;
}

export interface FetchHeatmapDetailOptions {
  precinct_num: string;
  period_start: string; // ISO date (YYYY-MM-DD)
  bucket?: 'daily' | 'weekly' | 'monthly';
  limit?: number;
  offset?: number;
}

/**
 * Fetch the drill-down detail for a single engagement-heatmap cell.
 *
 * The response is PII-light by design:
 *   - voter is represented only by a hint string ("Voter #382").
 *   - canvasser_name is only populated for COORDINATOR_PLUS_ROLES; other
 *     roles get null with the response-level flag `canvasser_names_redacted`.
 *
 * Pagination follows the standard offset/limit contract. When
 * `has_more === true`, call the endpoint again with `offset = next_offset`.
 */
export async function fetchEngagementHeatmapDetail(
  params: FetchHeatmapDetailOptions
): Promise<HeatmapDetailResponse> {
  const queryParams: Record<string, string | number> = {
    precinct_num: params.precinct_num,
    period_start: params.period_start,
  };
  if (params.bucket !== undefined) queryParams.bucket = params.bucket;
  if (params.limit !== undefined) queryParams.limit = params.limit;
  if (params.offset !== undefined) queryParams.offset = params.offset;

  const response = await client.get<HeatmapDetailResponse>(
    '/narrative/engagement-heatmap/detail',
    { params: queryParams }
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Error helper — consistent mapping from axios error → user-facing message
// ---------------------------------------------------------------------------

/**
 * Turn an axios error into a short string a view can display.
 * Mirrors the style of formatCanvassError from canvass.ts.
 *
 * The backend uses HTTP status codes consistently:
 *   401 — not authenticated
 *   403 — authenticated but role-walled
 *   404 — not found
 *   422 — validation error
 * Anything else is a backend bug or network failure.
 */
export function formatNarrativeError(err: unknown): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = err as any;
  const status = e?.response?.status as number | undefined;
  const detail = e?.response?.data?.detail as string | undefined;
  if (status === 401) {
    return 'Not authenticated. Log in and try again.';
  }
  if (status === 403) {
    return (
      'Access Denied — Engagement Heatmap requires coordinator+ role.' +
      (detail ? ` (${detail})` : '')
    );
  }
  if (status === 404) {
    return detail || 'Endpoint not found.';
  }
  if (status === 422) {
    return detail || 'Invalid filter parameters.';
  }
  if (!status) {
    return 'Could not reach the backend.';
  }
  return detail || `Backend error ${status}.`;
}
